import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { FluxoBlock } from "@/hooks/useFluxos";
import { deleteFluxoMediaByUrl } from "@/lib/fluxoMedia";
import BlockCard, { type AssistantOption } from "./BlockCard";

export const FLUXO_CANVAS_ID = "fluxo-canvas";

interface BlockListProps {
  blocks: FluxoBlock[];
  assistants: AssistantOption[];
  accountId?: string | null;
  fluxoId?: string | null;
  onBlocksChange: (blocks: FluxoBlock[]) => void;
}

const BlockList: React.FC<BlockListProps> = ({
  blocks,
  assistants,
  accountId,
  fluxoId,
  onBlocksChange,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: FLUXO_CANVAS_ID });

  const handleBlockChange = (updated: FluxoBlock) => {
    onBlocksChange(blocks.map((b) => (b.id === updated.id ? updated : b)));
  };

  const handleBlockRemove = (id: string) => {
    const removed = blocks.find((b) => b.id === id);
    if (removed && (removed.type === "imagem" || removed.type === "audio")) {
      void deleteFluxoMediaByUrl(removed.url);
    }
    onBlocksChange(blocks.filter((b) => b.id !== id));
  };

  return (
    <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        className={`flex flex-col gap-3 min-h-[160px] rounded-[12px] transition-shadow ${
          isOver ? "ring-2 ring-[#00a884]/50" : ""
        }`}
      >
        {blocks.length === 0 ? (
          <div className="flex-1 border border-dashed border-[#CBD5E1] rounded-[12px] p-8 text-center flex flex-col items-center justify-center bg-[#F8FAFC]/60 dark:border-[#3F3F46] dark:bg-[#18181B]/50">
            <p className="text-[#0F172A] font-bold mb-1 dark:text-neutral-50 dark:font-semibold">Fluxo vazio</p>
            <p className="text-[#64748B] text-sm dark:text-neutral-400">
              Clique ou arraste blocos do menu ao lado para montar a sequência do seu fluxo.
            </p>
          </div>
        ) : (
          blocks.map((block) => (
            <BlockCard
              key={block.id}
              block={block}
              assistants={assistants}
              accountId={accountId}
              fluxoId={fluxoId}
              onChange={handleBlockChange}
              onRemove={handleBlockRemove}
            />
          ))
        )}
      </div>
    </SortableContext>
  );
};

export default BlockList;
